import { type MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { useCommandPaletteIsOpen } from '@atlassian/jira-command-palette/src/controllers/command-palette/index.tsx';
import {
	useAnalyticsEvents,
	fireTrackAnalyticsDeferred,
} from '@atlassian/jira-product-analytics-bridge';
import type { QuickSearchResponseData } from '../../common/types.tsx';
import { useQuickSearch } from '../../services/quick-search/index.tsx';

// The recentCache has to be a ref to ensure getResponse doesn't change
type RecentCache = Promise<QuickSearchResponseData> | undefined;

export const useRecentCache = (): [MutableRefObject<RecentCache>, (cache: RecentCache) => void] => {
	const recentCacheRef = useRef<RecentCache>();
	const setRecentCache = useCallback(
		(cache: RecentCache) => {
			recentCacheRef.current = cache;
		},
		[recentCacheRef],
	);
	return [recentCacheRef, setRecentCache];
};

type Fetch = (
	searchString: string,
	sessionId: string,
	signal?: AbortSignal,
) => Promise<{ result: QuickSearchResponseData; isCache: boolean }>;

export const useQuickSearchWithRecentCache = () => {
	const [{ isOpen }] = useCommandPaletteIsOpen();
	const [recentCache, setRecentCache] = useRecentCache();
	const { fetch } = useQuickSearch();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Reset cache when compal closes
	useEffect(() => {
		if (Boolean(recentCache.current) && !isOpen) {
			setRecentCache(undefined);
		}
	}, [isOpen, recentCache, setRecentCache]);

	const fetchData = useCallback(
		async (searchString: string, sessionId: string, signal?: AbortSignal) => {
			const start = new Date();
			const apiCall = fetch(searchString, sessionId, signal);
			const result = await apiCall;
			const end = new Date();
			const duration = end.getTime() - start.getTime();
			if (searchString?.length) {
				fireTrackAnalyticsDeferred(createAnalyticsEvent({}), 'searchRequest completed', {
					postQueryRequestDurationMs: duration,
				});
			} else {
				fireTrackAnalyticsDeferred(createAnalyticsEvent({}), 'recentRequest completed', {
					preQueryRequestDurationMs: duration,
				});
				setRecentCache(apiCall);
			}
			return result;
		},
		[createAnalyticsEvent, fetch, setRecentCache],
	);

	const getResponse: Fetch = useCallback(
		async (searchString: string, sessionId: string, signal?: AbortSignal) => {
			if (searchString && searchString.length > 0) {
				const result = await fetchData(searchString, sessionId, signal);
				return {
					result,
					isCache: false,
				};
			}
			if (recentCache.current) {
				// refetch if existing cache is in error state
				try {
					await recentCache.current;
				} catch (err) {
					const apiCall = fetchData(searchString, sessionId, signal);
					return {
						result: await apiCall,
						isCache: false,
					};
				}
			} else {
				const apiCall = fetchData(searchString, sessionId, signal);
				return {
					result: await apiCall,
					isCache: false,
				};
			}
			return {
				result: await recentCache.current,
				isCache: true,
			};
		},
		[recentCache, fetchData],
	);

	return {
		fetch: getResponse,
	};
};
